// src/pages/CompanyProfileForm.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../axios';
import {
  Container, Box, TextField, FormControlLabel, Switch, Button, Avatar,
  Alert, CircularProgress, Grid, Typography, Paper, IconButton, Dialog, DialogContent
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export default function CompanyProfileForm() {
  const { companyId: companyIdParam } = useParams();
  const navigate = useNavigate();
  const companyId = parseInt(companyIdParam, 10) || parseInt(localStorage.getItem('companyId'), 10) || 0;

  const [profile, setProfile] = useState({
    nameAr: '', nameEn: '', vatNo: '', crn: '', crType: 'CRN', activityAr: '',
    activityEn: '', address: '', buildingNo: '', streetName: '', district: '',
    city: '', additionalNo: '', postalCode: '', phone: '', mobile: '',
    email: '', website: '', enableZatcaInvoice: false, logoImage: null
  });

  const [logoPreview, setLogoPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [error, setError] = useState('');
  const [successModal, setSuccessModal] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/CompanyProfile/${companyId}`)
      .then((res) => {
        const data = res.data;
        setProfile({ ...data, logoImage: null });
        setLogoPreview(data.logoImage ? `data:image/png;base64,${data.logoImage}` : '');
      })
      .catch((err) => {
        if (err.response?.status === 404) setIsNew(true);
        else setError('فشل جلب بيانات المنشأة.');
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setLogoPreview(dataUrl);
      setProfile(prev => ({ ...prev, logoImage: dataUrl.split(',')[1] }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const payload = { companyId, ...profile };
      if (isNew) await api.post('/CompanyProfile', payload);
      else await api.put(`/CompanyProfile/${companyId}`, payload);
      setSuccessModal(true);
      setTimeout(() => { setSuccessModal(false); navigate('/dashboard'); }, 2000);
    } catch (err) { setError('حدث خطأ أثناء الحفظ.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;

  return (
    <Container maxWidth="lg" dir="rtl" sx={{ py: 4 }}>
      <form onSubmit={handleSubmit}>
        {/* شريط علوي موحد: الشعار + الإعدادات + أزرار التحكم */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={logoPreview} sx={{ width: 60, height: 60, border: '1px solid #ddd' }} />
            <IconButton component="label"><PhotoCamera /><input hidden type="file" onChange={handleLogoChange} /></IconButton>
            <FormControlLabel
              control={<Switch name="enableZatcaInvoice" checked={profile.enableZatcaInvoice} onChange={handleChange} />}
              label={<Typography variant="body2" fontWeight="bold">تفعيل ZATCA</Typography>}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => navigate(-1)}>إلغاء</Button>
            <Button type="submit" variant="contained" disabled={submitting} startIcon={<SaveIcon />}>
              {submitting ? 'جاري الحفظ...' : 'حفظ التغييرات'}
            </Button>
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>إعدادات المنشأة</Typography>

        {/* الحقول مرتبة في صفوف */}
        <Paper sx={{ p: 4, mb: 3, borderRadius: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}><BusinessIcon color="primary" /><Typography variant="h6" fontWeight="bold">البيانات التعريفية</Typography></Box>
          <Grid container spacing={3}>
            <Grid item xs={6}><TextField fullWidth label="اسم المنشأة (عربي)" name="nameAr" value={profile.nameAr} onChange={handleChange} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Name (English)" name="nameEn" value={profile.nameEn} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="الرقم الضريبي" name="vatNo" value={profile.vatNo} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="السجل التجاري" name="crn" value={profile.crn} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="النشاط التجاري" name="activityAr" value={profile.activityAr} onChange={handleChange} /></Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 4, mb: 3, borderRadius: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}><ContactPhoneIcon color="primary" /><Typography variant="h6" fontWeight="bold">معلومات الاتصال</Typography></Box>
          <Grid container spacing={3}>
            <Grid item xs={3}><TextField fullWidth label="الهاتف" name="phone" value={profile.phone} onChange={handleChange} /></Grid>
            <Grid item xs={3}><TextField fullWidth label="الجوال" name="mobile" value={profile.mobile} onChange={handleChange} /></Grid>
            <Grid item xs={3}><TextField fullWidth label="البريد الإلكتروني" name="email" value={profile.email} onChange={handleChange} /></Grid>
            <Grid item xs={3}><TextField fullWidth label="الموقع الإلكتروني" name="website" value={profile.website} onChange={handleChange} /></Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 4, borderRadius: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}><LocationOnIcon color="primary" /><Typography variant="h6" fontWeight="bold">العنوان الوطني</Typography></Box>
          <Grid container spacing={3}>
            <Grid item xs={4}><TextField fullWidth label="المدينة" name="city" value={profile.city} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="الحي" name="district" value={profile.district} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="الشارع" name="streetName" value={profile.streetName} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="رقم المبنى" name="buildingNo" value={profile.buildingNo} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="الرقم الفرعي" name="additionalNo" value={profile.additionalNo} onChange={handleChange} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="الرمز البريدي" name="postalCode" value={profile.postalCode} onChange={handleChange} /></Grid>
          </Grid>
        </Paper>
      </form>

      <Dialog open={successModal} PaperProps={{ sx: { borderRadius: 4, p: 3, textAlign: 'center' } }}>
        <DialogContent>
          <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" fontWeight="bold">تم الحفظ بنجاح!</Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
}